import React from 'react';
import { Formik, Form, Field, CccisdInput, CccisdFieldWrapper, CccisdToggle } from 'cccisd-formik';
import Table from 'cccisd-graphql-table';
import PropTypes from 'prop-types';
import { UserCheck } from 'cccisd-laravel-nexus';

import sitesQuery from './sites.graphql';
import RequiredFieldLabel from 'js/components/CareCoordinatorForm/RequiredFieldLabel';
import style from './style.css';

const siteColumns = [
    {
        name: 'group.groupId',
        label: 'ID',
        hideInTable: true,
    },
    {
        name: 'group.label',
        label: 'Site',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.agency.group.label',
        label: 'Agency',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.organization.group.label',
        label: 'Organization',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.collaborator.group.label',
        label: 'Collaborator',
        sort: true,
        filter: true,
    },
];

const Fortress = window.cccisd && window.cccisd.fortress;

const isEmail = email => /\S+@\S+\.\S+/.test(email);

const SupervisorForm = props => {
    const { onSubmit, initialValues, isEdit } = props;
    const [notice, setNotice] = React.useState({ text: '', level: 'success' });

    // Hides/skips validation of the site picker for single-site roles.
    const currentRole = Fortress.user && Fortress.user.acting.role.handle;
    const hasFixedSite = currentRole === 'supervisor' || currentRole === 'siteAdmin';

    // pre-fill the Site column's filter with the supervisor's current site,
    // so the currently-assigned row is visible without searching through every site
    const siteFilter = initialValues.siteName ? { 'group.label': initialValues.siteName } : {};

    /**
     * When an email is entered into the email field,
     * check if the username exists already and is available
     * to use with this username/group combination.
     *
     * Also check when the group is selected or changed.
     */
    const userCheck = async (values, newGroup, setFieldValue) => {
        const checkUserDelay = 1000;
        const email = values.email;
        const group = newGroup || (Array.isArray(values.site) ? values.site[0] : null);

        if (!email || !group) {
            return;
        }

        // needed on submit
        setFieldValue('existingUser', false);
        setFieldValue('available', true);

        const checkUserData = {
            username: email,
            group,
            role: 'supervisor',
        };

        const userCheckResponse = await UserCheck(checkUserData, checkUserDelay);

        if (userCheckResponse.available) {
            // show username is available
            setNotice({ text: 'Username is available!', level: 'success' });
            // check if existing user
            // if yes, pre populate first and last name
            if (userCheckResponse.user) {
                setFieldValue('existingUser', true);

                setNotice({
                    text: 'User already exists in another group, filling details',
                    level: 'info',
                });
                setFieldValue('firstName', userCheckResponse.user.first_name || '');
                setFieldValue('lastName', userCheckResponse.user.last_name || '');
            }
        } else {
            setFieldValue('existingUser', true);
            setFieldValue('available', false);
            // user is not available
            // maybe same group?
            // show error on form
            setNotice({
                text: 'User already exists in this group',
                level: 'danger',
            });
        }
    };

    const onHandleSubmit = async values => {
        await onSubmit({ ...values, site: values.site[0], role: 'supervisor' });
    };

    const validate = values => {
        const errors = {};

        if (!values.firstName) {
            errors.firstName = 'First Name is required.';
        }
        if (!values.lastName) {
            errors.lastName = 'Last Name is required.';
        }
        if (!isEdit && !values.email) {
            errors.email = 'Email is required.';
        } else if (values.email && !isEmail(values.email)) {
            errors.email = 'Please enter a valid email.';
        }
        if (!hasFixedSite) {
            if (values.site.length < 1) {
                errors.site = 'Site is required.';
            }
            if (values.site.length > 1) {
                errors.site = 'You may select only 1 Site.';
            }
        }

        return errors;
    };

    return (
        <Formik
            onSubmit={e => onHandleSubmit(e)}
            validate={e => validate(e)}
            initialValues={initialValues}
            enableReinitialize
        >
            {({ handleSubmit, setFieldValue, isSubmitting, values }) => (
                <Form>
                    {notice.text && <div className={`alert alert-${notice.level}`}>{notice.text}</div>}
                    <Field name="email">
                        {({ field, form }) => (
                            <CccisdInput
                                field={field}
                                form={form}
                                label={<RequiredFieldLabel isRequired>Email:</RequiredFieldLabel>}
                                disabled={Boolean(isEdit)}
                                autoFocus={!isEdit}
                                onBlur={event => {
                                    field.onBlur(event);
                                    userCheck(form.values, null, form.setFieldValue);
                                }}
                            />
                        )}
                    </Field>
                    <Field
                        name="firstName"
                        component={CccisdInput}
                        label={<RequiredFieldLabel isRequired>First Name:</RequiredFieldLabel>}
                    />
                    <Field
                        name="lastName"
                        component={CccisdInput}
                        label={<RequiredFieldLabel isRequired>Last Name:</RequiredFieldLabel>}
                    />

                    <p>
                        <strong>Permissions:</strong>
                    </p>
                    <Field name="managePageAccess" component={CccisdToggle} label="Manage Page Access" />
                    <Field name="dashboardPageAccess" component={CccisdToggle} label="Dashboard Page Access" />
                    <Field name="administerPageAccess" component={CccisdToggle} label="Administer Page Access" />

                    {!hasFixedSite && (
                        <Field name="site">
                            {({ field, form }) => (
                                <CccisdFieldWrapper
                                    field={field}
                                    form={form}
                                    label={<RequiredFieldLabel isRequired>Site:</RequiredFieldLabel>}
                                >
                                    <Table
                                        query={sitesQuery}
                                        rowKey="group.groupId"
                                        columns={siteColumns}
                                        csvFilename="Sites.csv"
                                        showRowActions={false}
                                        perPage={10}
                                        filter={siteFilter}
                                        onRowClick={row => {
                                            setFieldValue('site', [row['group.groupId']]);
                                            userCheck(values, [row['group.groupId']], setFieldValue);
                                        }}
                                        setRowClass={row =>
                                            row['group.groupId'] === field.value[0] && style.selectedRow
                                        }
                                    />
                                </CccisdFieldWrapper>
                            )}
                        </Field>
                    )}

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '1em',
                        }}
                    >
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleSubmit()}
                            disabled={isSubmitting}
                        >
                            Submit
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

SupervisorForm.propTypes = {
    onSubmit: PropTypes.func,
    initialValues: PropTypes.object,
    isEdit: PropTypes.bool,
};

export default SupervisorForm;
